import { useCallback, useRef, useState } from 'react';

import { useEvent, useFormField } from '@american-autonomy/ag-ui';

import type { FieldValues, Item, Props } from './types';

const useSelectField = <T extends FieldValues>({
    control,
    isOpen: controlledIsOpen,
    isRequired = false,
    name,
    onPanelClose,
    onPanelOpen,
    ref,
    requiredMessage
}: Props<T>) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const isPanelOpen = controlledIsOpen ?? internalIsOpen;

    const {
        field: { onChange, value }
    } = useFormField({
        control,
        name,
        rules: {
            required: isRequired ? requiredMessage : false
        }
    });

    const handleClose = useCallback(
        (selectedValue?: Item) => {
            if (controlledIsOpen === undefined) {
                setInternalIsOpen(false);
            }
            onPanelClose?.(selectedValue);
        },
        [controlledIsOpen, onPanelClose]
    );

    const handleToggle = () => {
        if (isPanelOpen) {
            handleClose(value);
        } else {
            if (controlledIsOpen === undefined) {
                setInternalIsOpen(true);
            }
            onPanelOpen?.();
        }
    };

    const handleClickOutside = useCallback(
        (event: Event) => {
            const target = event.target as HTMLElement;

            if (!containerRef.current?.contains(target) && isPanelOpen) {
                handleClose(value);
            }
        },
        [isPanelOpen, handleClose, value]
    );

    useEvent({ event: 'mouseup', handler: !ref ? handleClickOutside : () => {} });

    return {
        containerRef,
        handleClose,
        handleToggle,
        isPanelOpen,
        onChange,
        value
    };
};

export default useSelectField;
